/**
 * SecurityLogger Service
 * Handles logging of authentication events and security monitoring
 */

class SecurityLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 1000; // Keep last 1000 logs in memory
    this.suspiciousThresholds = {
      failedAttempts: 5, // 5 failed attempts in 15 minutes
      timeWindow: 15 * 60 * 1000, // 15 minutes in milliseconds
      ipAttempts: 50, // 50 attempts from same IP in 1 hour
      ipTimeWindow: 60 * 60 * 1000, // 1 hour in milliseconds
    };
  }

  /**
   * Log a login attempt
   * @param {Object} attemptData - Login attempt data
   * @param {string} attemptData.email - User email
   * @param {boolean} attemptData.success - Whether login was successful
   * @param {string} attemptData.ipAddress - User's IP address
   * @param {string} attemptData.userAgent - User's browser info
   * @param {string} attemptData.sessionId - Session identifier
   * @param {string} attemptData.timestamp - Timestamp of attempt
   * @param {string} attemptData.errorCode - Error code if failed
   * @param {string} attemptData.mfaUsed - Whether MFA was used
   */
  async logLoginAttempt(attemptData) {
    const logEntry = {
      id: this.generateLogId(),
      timestamp: attemptData.timestamp || new Date().toISOString(),
      type: 'LOGIN_ATTEMPT',
      email: attemptData.email,
      success: attemptData.success,
      ipAddress: attemptData.ipAddress || this.getClientIP(),
      userAgent: attemptData.userAgent || navigator.userAgent,
      sessionId: attemptData.sessionId || this.generateSessionId(),
      errorCode: attemptData.errorCode || null,
      mfaUsed: attemptData.mfaUsed || false,
      riskScore: this.calculateRiskScore(attemptData),
    };

    // Add to local logs
    this.logs.unshift(logEntry);
    
    // Keep only the most recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // Check for suspicious activity
    await this.checkSuspiciousActivity(logEntry);

    // Send to external logging service (AWS CloudWatch, etc.)
    await this.sendToLoggingService(logEntry);

    return logEntry;
  }

  /**
   * Log MFA events
   * @param {Object} mfaData - MFA event data
   */
  async logMFAEvent(mfaData) {
    const logEntry = {
      id: this.generateLogId(),
      timestamp: new Date().toISOString(),
      type: 'MFA_EVENT',
      email: mfaData.email,
      event: mfaData.event, // 'SETUP', 'VERIFY_SUCCESS', 'VERIFY_FAILED', 'DISABLE'
      ipAddress: mfaData.ipAddress || this.getClientIP(),
      userAgent: mfaData.userAgent || navigator.userAgent,
      sessionId: mfaData.sessionId || this.generateSessionId(),
    };

    this.logs.unshift(logEntry);
    await this.sendToLoggingService(logEntry);
    return logEntry;
  }

  /**
   * Log security events
   * @param {Object} securityData - Security event data
   */
  async logSecurityEvent(securityData) {
    const logEntry = {
      id: this.generateLogId(),
      timestamp: new Date().toISOString(),
      type: 'SECURITY_EVENT',
      event: securityData.event, // 'SUSPICIOUS_ACTIVITY', 'RATE_LIMIT_EXCEEDED', 'ACCOUNT_LOCKED'
      email: securityData.email,
      ipAddress: securityData.ipAddress || this.getClientIP(),
      userAgent: securityData.userAgent || navigator.userAgent,
      details: securityData.details || {},
      severity: securityData.severity || 'MEDIUM', // LOW, MEDIUM, HIGH, CRITICAL
    };

    this.logs.unshift(logEntry);
    await this.sendToLoggingService(logEntry);
    return logEntry;
  }

  /**
   * Calculate risk score for login attempt
   * @param {Object} attemptData - Login attempt data
   * @returns {number} Risk score (0-100)
   */
  calculateRiskScore(attemptData) {
    let riskScore = 0;

    // Failed attempts increase risk
    if (!attemptData.success) {
      riskScore += 20;
    }

    // Check recent failed attempts for this email
    const recentFailedAttempts = this.getRecentFailedAttempts(attemptData.email);
    riskScore += Math.min(recentFailedAttempts * 10, 50);

    // Check recent attempts from this IP
    const recentIPAttempts = this.getRecentIPAttempts(attemptData.ipAddress);
    riskScore += Math.min(recentIPAttempts * 5, 30);

    // MFA usage decreases risk
    if (attemptData.mfaUsed) {
      riskScore -= 15;
    }

    // New IP address increases risk slightly
    if (this.isNewIP(attemptData.ipAddress)) {
      riskScore += 10;
    }

    return Math.max(0, Math.min(100, riskScore));
  }

  /**
   * Check for suspicious activity patterns
   * @param {Object} logEntry - Log entry to analyze
   */
  async checkSuspiciousActivity(logEntry) {
    const suspiciousActivities = [];

    // Check for multiple failed attempts
    const recentFailedAttempts = this.getRecentFailedAttempts(logEntry.email);
    if (recentFailedAttempts >= this.suspiciousThresholds.failedAttempts) {
      suspiciousActivities.push({
        type: 'MULTIPLE_FAILED_ATTEMPTS',
        email: logEntry.email,
        count: recentFailedAttempts,
        severity: 'HIGH'
      });
    }

    // Check for IP-based attacks
    const recentIPAttempts = this.getRecentIPAttempts(logEntry.ipAddress);
    if (recentIPAttempts >= this.suspiciousThresholds.ipAttempts) {
      suspiciousActivities.push({
        type: 'IP_BASED_ATTACK',
        ipAddress: logEntry.ipAddress,
        count: recentIPAttempts,
        severity: 'CRITICAL'
      });
    }

    // Check for rapid successive attempts
    const rapidAttempts = this.getRapidAttempts(logEntry.email, logEntry.ipAddress);
    if (rapidAttempts >= 3) {
      suspiciousActivities.push({
        type: 'RAPID_ATTEMPTS',
        email: logEntry.email,
        ipAddress: logEntry.ipAddress,
        count: rapidAttempts,
        severity: 'MEDIUM'
      });
    }

    // Log suspicious activities
    for (const activity of suspiciousActivities) {
      await this.logSecurityEvent({
        event: 'SUSPICIOUS_ACTIVITY',
        email: logEntry.email,
        ipAddress: logEntry.ipAddress,
        details: activity,
        severity: activity.severity
      });
    }

    return suspiciousActivities;
  }

  /**
   * Get recent failed attempts for an email
   * @param {string} email - User email
   * @returns {number} Number of failed attempts
   */
  getRecentFailedAttempts(email) {
    const cutoffTime = new Date(Date.now() - this.suspiciousThresholds.timeWindow);
    
    return this.logs.filter(log => 
      log.type === 'LOGIN_ATTEMPT' &&
      log.email === email &&
      !log.success &&
      new Date(log.timestamp) > cutoffTime
    ).length;
  }

  /**
   * Get recent attempts from an IP
   * @param {string} ipAddress - IP address
   * @returns {number} Number of attempts
   */
  getRecentIPAttempts(ipAddress) {
    const cutoffTime = new Date(Date.now() - this.suspiciousThresholds.ipTimeWindow);
    
    return this.logs.filter(log => 
      log.type === 'LOGIN_ATTEMPT' &&
      log.ipAddress === ipAddress &&
      new Date(log.timestamp) > cutoffTime
    ).length;
  }

  /**
   * Get rapid successive attempts
   * @param {string} email - User email
   * @param {string} ipAddress - IP address
   * @returns {number} Number of rapid attempts
   */
  getRapidAttempts(email, ipAddress) {
    const cutoffTime = new Date(Date.now() - 5 * 60 * 1000); // 5 minutes
    
    return this.logs.filter(log => 
      log.type === 'LOGIN_ATTEMPT' &&
      (log.email === email || log.ipAddress === ipAddress) &&
      new Date(log.timestamp) > cutoffTime
    ).length;
  }

  /**
   * Check if IP is new (not seen before)
   * @param {string} ipAddress - IP address
   * @returns {boolean} Whether IP is new
   */
  isNewIP(ipAddress) {
    return !this.logs.some(log => log.ipAddress === ipAddress);
  }

  /**
   * Send log entry to external logging service
   * @param {Object} logEntry - Log entry to send
   */
  async sendToLoggingService(logEntry) {
    try {
      // In a real implementation, this would send to AWS CloudWatch, Splunk, etc.
      // For now, we'll just log to console and could send to a custom API
      console.log('Security Log:', logEntry);
      
      // Example: Send to custom API endpoint
      // await fetch('/api/security-logs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(logEntry)
      // });
    } catch (error) {
      console.error('Failed to send log to external service:', error);
    }
  }

  /**
   * Get client IP address (simplified)
   * @returns {string} Client IP
   */
  getClientIP() {
    // In a real implementation, this would be passed from the server
    // For now, return a placeholder
    return 'client-ip-placeholder';
  }

  /**
   * Generate unique log ID
   * @returns {string} Unique log ID
   */
  generateLogId() {
    return `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate session ID
   * @returns {string} Session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get recent logs for analysis
   * @param {number} limit - Number of logs to return
   * @returns {Array} Recent logs
   */
  getRecentLogs(limit = 100) {
    return this.logs.slice(0, limit);
  }

  /**
   * Get logs by type
   * @param {string} type - Log type
   * @returns {Array} Logs of specified type
   */
  getLogsByType(type) {
    return this.logs.filter(log => log.type === type);
  }

  /**
   * Clear old logs
   * @param {number} olderThan - Clear logs older than this timestamp
   */
  clearOldLogs(olderThan = Date.now() - 24 * 60 * 60 * 1000) { // Default: 24 hours
    this.logs = this.logs.filter(log => new Date(log.timestamp) > new Date(olderThan));
  }
}

// Create singleton instance
const securityLogger = new SecurityLogger();

export default securityLogger;
